/**
 * @file Purchase PPD Print Package Cart Item Container.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';

import {
  selectPrintPackage as selectPrintPackageAction,
  deselectPrintPackage as deselectPrintPackageAction,
} from '../../actions/ppd-purchase';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import Button from '../../components/global/Button';
import TrashIcon from '../../components/icons/TrashIcon';
import { formatPriceForDisplay } from '../../util/format-money';
import { DIGITAL_DOWNLOADS_MODE } from '../../constants/print-packages';
import PlusCircleIcon from '../../components/icons/PlusCircleIcon';
import StopIcon from '../../components/icons/StopIcon';
import Photo from '../../components/global/Photo';
import Alert from '../../components/global/Alert';
import CustomControl from '../../components/global/CustomControl';
import { requestConfirm as requestConfirmAction } from '../../actions/modal-actions';
import { AlbumForPpdPurchase } from '../../graphql/queries/albums';
import { MeForAlbumPurchase } from '../../graphql/queries/account';
import { hasSubscriptionInCart } from '../../util/ppd-purchase';
import {
  getPrintPackageItemLabel,
  sortPrintPackageItems,
} from '../../util/print-packages';

class PurchasePpdPrintPackageCartItem extends Component {
  static propTypes = {
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    ),
    album: PropTypes.shape({
      id: PropTypes.string,
      photo_pricing: PropTypes.shape({
        id: PropTypes.string,
        requires_package_purchase: PropTypes.bool,
      }),
    }),
    cartItem: PropTypes.shape({
      type: PropTypes.oneOf([PPD_CART_ITEM_TYPES.PRINT_PACKAGE]),
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
      printPackage: PropTypes.shape({
        id: PropTypes.string.isRequired,
        print_package: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          items: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string.isRequired,
                quantity: PropTypes.number,
                type: PropTypes.shape({
                  name: PropTypes.string,
                  min_price: PropTypes.shape({
                    value: PropTypes.number,
                  }),
                }),
              }),
            ),
          }),
          digital_downloads_mode: PropTypes.oneOf(
            Object.values(DIGITAL_DOWNLOADS_MODE),
          ),
          digital_downloads_upsell_price: PropTypes.shape({
            value: PropTypes.number,
          }),
        }),
      }),
      quantity: PropTypes.number,
      digitalDownloadUpsell: PropTypes.bool,
    }),
    selectPrintPackage: PropTypes.func,
    deselectPrintPackage: PropTypes.func,
    requestConfirm: PropTypes.func,
    user: PropTypes.shape({
      id: PropTypes.string,
      purchased_print_packages: PropTypes.shape({
        total: PropTypes.number,
      }),
    }),
  };

  render() {
    const {
      cartItem,
      selectPrintPackage,
      deselectPrintPackage,
      cart,
      requestConfirm,
      album,
      user,
    } = this.props;

    const printPackage = get(cartItem, 'printPackage.print_package');
    const { thumbnail_urls: thumbs } = cartItem.photo;
    const price = formatPriceForDisplay(get(printPackage, 'price.value', 0));
    const items = sortPrintPackageItems(get(printPackage, 'items.records', []));

    const hasSubscriptionSelected = hasSubscriptionInCart(
      cart,
      get(cartItem, 'selectedIdentity.id'),
    );

    const isLastPackageItem =
      cart.filter(
        item =>
          item.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE &&
          item.selectedIdentity.id === cartItem.selectedIdentity.id,
      ).length === 1;

    const requiresPackagePurchase = get(
      album,
      'photo_pricing.requires_package_purchase',
      false,
    );
    const hasUserPurchasedPackage = !!get(
      user,
      'purchased_print_packages.records',
      [],
    ).find(
      purchase =>
        get(purchase, 'identity.id') ===
        get(cartItem, 'selectedIdentity.identity.id'),
    );

    const multipleItemsForIdentity =
      cart.filter(
        item => item.selectedIdentity.id === cartItem.selectedIdentity.id,
      ).length > 1;

    return (
      <div className="ppd-cart-item py-2 px-1 ppd-cart-item-print-package">
        <div className="d-flex">
          <div className="cart-item-image">
            <Photo
              key={cartItem.photo.id}
              className="h-100"
              imgClassName="image-cover-img"
              thumbs={thumbs}
              blockCapture
              lowestQuality
            />
            <div className="cart-item-image-quantity">
              <span className="text-xs">QTY</span>
              <span className="text-lg">{cartItem.quantity}</span>
            </div>
          </div>
          <div className="cart-item-desc">
            <div className="d-flex justify-content-between">
              <p className="text-sm text-dark mb-0">{printPackage.name}</p>
            </div>
            <div className="d-block text-muted text-xs mb-1">
              {items.map(item => (
                <span className="d-block" key={getPrintPackageItemLabel(item)}>
                  {item.quantity} × {getPrintPackageItemLabel(item)}
                </span>
              ))}
              {printPackage.digital_downloads_mode ===
                DIGITAL_DOWNLOADS_MODE.INCLUDED && (
                <span className="d-block text-bold">
                  Includes Hi-Res Download for this photo
                </span>
              )}
              {printPackage.digital_downloads_mode ===
                DIGITAL_DOWNLOADS_MODE.UPSELL &&
                hasSubscriptionSelected && (
                  <span className="d-block text-bold">
                    Hi-Res Download Included with Subscription
                  </span>
                )}
            </div>
            <div className="d-flex justify-content-between">
              <p className="text-sm text-bold text-dark mb-0">${price}</p>
              <div className="cart-item-actions">
                <Button
                  btnStyle="reset"
                  disabled={cartItem.quantity === 1}
                  onClick={() =>
                    selectPrintPackage(
                      cartItem.photo,
                      cartItem.printPackage, // Avoid doing -1 or 0
                      Math.max(1, cartItem.quantity - 1),
                      cartItem.digitalDownloadUpsell,
                    )
                  }
                >
                  <StopIcon size="25" color="#0d84fe" />
                </Button>
                <Button
                  btnStyle="reset"
                  onClick={() =>
                    selectPrintPackage(
                      cartItem.photo,
                      cartItem.printPackage,
                      cartItem.quantity + 1,
                      cartItem.digitalDownloadUpsell,
                    )
                  }
                >
                  <PlusCircleIcon size="25" color="#0d84fe" />
                </Button>
                <Button
                  className="remove-cart-item"
                  btnStyle="reset"
                  onClick={() => {
                    if (
                      requiresPackagePurchase &&
                      isLastPackageItem &&
                      !hasUserPurchasedPackage &&
                      multipleItemsForIdentity
                    ) {
                      requestConfirm({
                        message:
                          'Removing this print package will also remove all prints & digital downloads of this identity from your cart. Are you sure you want to do this?',
                        confirmText: 'Yes, Remove',
                        onConfirm: () => {
                          deselectPrintPackage(
                            cartItem.photo,
                            cartItem.printPackage,
                          );
                        },
                        onCancel: () => {},
                        confirmBtnStyle: 'danger',
                      });
                    } else {
                      deselectPrintPackage(
                        cartItem.photo,
                        cartItem.printPackage,
                      );
                    }
                  }}
                >
                  <TrashIcon size={23} color="#f1524b" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        {printPackage.digital_downloads_mode ===
          DIGITAL_DOWNLOADS_MODE.UPSELL &&
          !hasSubscriptionSelected && (
            <Alert
              type="info"
              className="d-flex align-items-center mt-1 text-xs"
            >
              <span>Include Hi-Res Download for</span>
              <span className="text-bold ml-1">
                $
                {formatPriceForDisplay(
                  get(printPackage, 'digital_downloads_upsell_price.value', 0),
                )}
              </span>
              <CustomControl
                type="checkbox"
                onChange={() => {
                  selectPrintPackage(
                    cartItem.photo,
                    cartItem.printPackage,
                    cartItem.quantity,
                    !cartItem.digitalDownloadUpsell,
                  );
                }}
                checked={cartItem.digitalDownloadUpsell}
                className="ml-auto mr-0"
                labelSrOnly
                id={`${cartItem.photo.id}-${cartItem.printPackage.id}`}
              />
            </Alert>
          )}
      </div>
    );
  }
}

const withAlbumForPpdPurchase = graphql(AlbumForPpdPurchase, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    loading,
  }),
});

const mapStateToProps = ({ ppdPurchase }) => ({
  cart: ppdPurchase.cart,
});

const mapDispatchToProps = dispatch => ({
  selectPrintPackage: (
    photo,
    printPackage,
    quantity = 1,
    digitalDownloadUpsell = false,
  ) =>
    dispatch(
      selectPrintPackageAction(
        photo,
        printPackage,
        quantity,
        digitalDownloadUpsell,
      ),
    ),
  deselectPrintPackage: (photo, printPackage) =>
    dispatch(deselectPrintPackageAction(photo, printPackage)),
  requestConfirm: (...args) => dispatch(requestConfirmAction(...args)),
});

const withMeForAlbumPurchase = graphql(MeForAlbumPurchase, {
  options: ({ match }) => ({
    variables: {
      albumId: match.params.albumId,
    },
  }),
  props: ({ data: { me } }) => ({
    user: me,
  }),
});

export { PurchasePpdPrintPackageCartItem as PurchasePpdPrintPackageCartItemComp };

export default compose(
  withRouter,
  withAlbumForPpdPurchase,
  withMeForAlbumPurchase,
  connect(mapStateToProps, mapDispatchToProps),
)(PurchasePpdPrintPackageCartItem);
